import { fetchUserProfile } from "../api/settings";
import { fetchAuthToken } from "../api/auth";
import { getTenant } from "../utils/tenant";
import { browserStore } from "../utils/storageKeys";

// When `bypassSso` is true we append an empty `kc_idp_hint` param. Keycloak's Identity Provider
// Redirector authenticator only auto-redirects to the configured SSO IdP when it finds a hint to act
// on; an empty hint makes it fall through to the next step in the flow (the username/password form).
// This lets employees reach the internal login form
const keycloakRealmUrl = (tenant: string) =>
  `${process.env.KEYCLOAK_URL || "https://keycloak.ediphi.com"}/realms/${tenant}/protocol/openid-connect`;

export const getAuthUrl = (tenant: string, bypassSso = false) => {
  const clientId = "costlab";
  const responseType = "id_token";
  const scope = "openid";
  const nonce = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const redirectUri = encodeURIComponent(window.location.origin + "/redirect.html");
  const url = `${keycloakRealmUrl(tenant)}/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}&scope=${scope}&nonce=${nonce}`;
  return bypassSso ? `${url}&kc_idp_hint=` : url;
};

/**
 * Ends the Keycloak SSO session. Built from the realm base rather than from `getAuthUrl`, which
 * already carries a query string — appending a path to that produced an unreachable URL, so the
 * SSO session outlived sign-out while the local teardown appeared to succeed.
 */
export const getLogoutUrl = (tenant: string, idToken: string, postLogoutRedirectUri: string) =>
  `${keycloakRealmUrl(tenant)}/logout?id_token_hint=${idToken}` +
  `&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;

export const getAuthToken = async () => {
  const tenant = getTenant() ?? "";
  const idToken = browserStore.get("idToken") || "";
  console.log("Fetching x-auth token...", "tenant:", tenant);

  const result = await fetchAuthToken(idToken, tenant);
  if (!result.success) {
    throw result.error;
  }

  const xAuthToken = result.data!;
  console.log("Fetched x-auth token successfully.");

  // Store the tokens in localStorage
  browserStore.set("idToken", idToken);
  browserStore.set("xAuthToken", xAuthToken);

  // Fetch and store user profile
  await getUserProfile();
};

export const tokenIsValid = async () => {
  try {
    await getUserProfile();
    return true;
  } catch (error) {
    console.log("Auth token not valid: ", error);
  }
  return false;
};

/** Signs in with an Ediphi session token. The token is kept only if the API accepts it. */
export const signInWithSessionToken = async (sessionToken: string) => {
  // A token sign-in has no Keycloak session; an id token left from an earlier sign-in would send
  // logout to Keycloak.
  browserStore.remove("idToken");
  browserStore.set("xAuthToken", sessionToken);
  try {
    await getUserProfile();
  } catch (error) {
    browserStore.remove("xAuthToken");
    throw error;
  }
};

const getUserProfile = async () => {
  const profileResult = await fetchUserProfile();
  if (!profileResult.success) {
    throw profileResult.error;
  }
  const profile = profileResult.data;
  browserStore.set("userProfile", JSON.stringify(profile?.user || {}));
  browserStore.set("userEmail", profile?.user?.email || "");
};

/** Ediphi staff, by the domain of the signed-in email. Gates UI only; nothing server-side checks it. */
export const isEdiphiUser = (): boolean => (browserStore.get("userEmail") ?? "").toLowerCase().endsWith("@ediphi.com");

export const isUserAdmin = (): boolean => {
  try {
    const userProfileJson = browserStore.get("userProfile");
    if (!userProfileJson) return false;

    const userProfile = JSON.parse(userProfileJson);
    return userProfile?.admin === true;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};
